import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	OptionList,
	type OptionListHandle,
	type OptionListInputProps,
	PlainInput,
	Radio,
} from '@treams/treams-ui';

import {
	type OptionsUpdate as OptionsUpdateOf,
	replaceOptionText,
} from './feedbackQuestionOptionText';

const StyledRadio = styled(Radio.Option)`
	padding: 0;

	> div:first-child {
		margin-right: 0;
	}
`;

export type LocalOption = {
	key: string;
	id?: string | null;
	text: graphql.LocalizedString;
};

export function createOption(): LocalOption {
	return { key: crypto.randomUUID(), text: [] };
}

export type OptionsUpdate = OptionsUpdateOf<LocalOption>;

export { resolveOptionsUpdate } from './feedbackQuestionOptionText';

export type FeedbackQuestionOptionsHandle = OptionListHandle;

function displayedOptionText(option: LocalOption, languageID: string): string {
	const entry = option.text.find((item) => item.languageID === languageID);

	// An empty string is a cleared translation and must stay empty. Fall back only
	// when this language has no entry yet, so the source text stays visible.
	if (entry) {
		return entry.text;
	}

	return option.text.find((item) => item.text)?.text ?? '';
}

function OptionInput(props: {
	option: LocalOption;
	languageID: string;
	readOnly?: boolean;
	placeholder?: string;
	inputProps: OptionListInputProps;
	onChange: (text: string) => void;
}) {
	const text = displayedOptionText(props.option, props.languageID);

	return (
		<Box ref={props.inputProps.registerRef}>
			<PlainInput
				multiline={false}
				readOnly={props.readOnly}
				value={text}
				placeholder={props.placeholder}
				label={text || props.placeholder}
				onChange={props.onChange}
				onFocus={props.inputProps.onFocus}
				onKeyDown={props.inputProps.onKeyDown}
				autoFocus={props.inputProps.autoFocus}
			/>
		</Box>
	);
}

type FeedbackQuestionOptionsProps = {
	options: LocalOption[];
	languageID: string;
	active: boolean;
	readOnly?: boolean;
	autoFocus?: string;
	error?: string;
	onChange: (options: OptionsUpdate) => void;
	onFocusOption: (key: string) => void;
	onFocusPrevious?: () => void;
	onAdd: () => void;
};

function FeedbackQuestionOptions(
	props: FeedbackQuestionOptionsProps,
	ref: React.Ref<FeedbackQuestionOptionsHandle>,
) {
	const { t } = useTranslation();

	const updateOption = (key: string, text: string) => {
		props.onChange((current) => replaceOptionText(current, key, props.languageID, text));
	};

	return (
		<OptionList
			ref={ref}
			items={props.options}
			active={props.active}
			readOnly={props.readOnly}
			minItems={2}
			autoFocusKey={props.autoFocus?.startsWith('option:') ? props.autoFocus.slice(7) : undefined}
			error={props.error}
			addLabel={t('settings.feedbackLibrary.question.options.add')}
			createItem={createOption}
			onChange={(items) => props.onChange(items)}
			onFocusItem={props.onFocusOption}
			onFocusPrevious={props.onFocusPrevious}
			onAdd={props.onAdd}
			renderLeading={() => (
				<StyledRadio checked={false} onChange={() => {}} title="" description="" ml="2px" />
			)}
			renderInput={(option, inputProps) => (
				<OptionInput
					option={option}
					languageID={props.languageID}
					readOnly={props.readOnly}
					placeholder={t('settings.feedbackLibrary.question.options.placeholder')}
					inputProps={inputProps}
					onChange={(text) => updateOption(option.key, text)}
				/>
			)}
		/>
	);
}

export default forwardRef(FeedbackQuestionOptions);
